/**
 * Search Modal Component
 *
 */
class SearchModal extends HTMLElement {
  constructor() {
    super();
    this.searchFormContainer = this.querySelector('.searchform');
    this.searchQuery = this.querySelector('form[role="search"] [name="q"]');
    this.closeButton = this.querySelector('.search-modal__close-button');
    this.openSearchModal = [];
    this.isBound = false;
    this.onSearchFormKeyup = (event) => {
      event.code.toUpperCase() === 'ESCAPE' && this.close();
    };
    this.onToggleButtonClick = (event) => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      this.onToggleBtnClick(event);
    };
    this.onCloseButtonClick = this.close.bind(this);
  }

  connectedCallback() {
    if (this.isBound) return;

    this.openSearchModal = Array.from(document.querySelectorAll('.open-searchform'));

    if (this.searchFormContainer) {
      this.searchFormContainer.addEventListener('keyup', this.onSearchFormKeyup);
    }

    this.openSearchModal.forEach((toggleBtn) => {
      toggleBtn.addEventListener('click', this.onToggleButtonClick);
      toggleBtn.setAttribute('role', 'button');
      toggleBtn.setAttribute('aria-expanded', 'false');
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClick);
    }

    this.isBound = true;
  }

  disconnectedCallback() {
    if (!this.isBound) return;

    if (this.searchFormContainer) {
      this.searchFormContainer.removeEventListener('keyup', this.onSearchFormKeyup);
    }

    this.openSearchModal.forEach((toggleBtn) => {
      toggleBtn.removeEventListener('click', this.onToggleButtonClick);
    });

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClick);
    }

    document.body.removeEventListener('click', this.onBodyClickEvent);
    this.isBound = false;
  }

  /**
   * To check if search modal is open or closed
   * 
   * @returns {boolean} true/false
   */
  isOpen() {
    return this.searchFormContainer.hasAttribute('open');
  }

  /**
   * Toggle Search Modal on search Icon click
   *
   * @param {event} Event instance
   */
  onToggleBtnClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.searchFormContainer.hasAttribute('open')) {
      this.open(event);
      document.getElementsByTagName('site-overlay')[0].classList.add('overlay--body')
      this.topHeaderHeight = document.querySelector('[data-topbar]').clientHeight;
      this.headerHeight = document.querySelector('[data-header]').clientHeight
      document.querySelector('search-modal.header__search').style.top=`${this.topHeaderHeight}px`;
      document.getElementsByTagName('site-overlay')[0].style.top = `${this.headerHeight}px`;
      
    }else{
      this.close()
    }
  }

  /**
   * Close Search Modal on body click if not within the Modal
   *
   * @param {event} Event instance
   */
  onBodyClick(event) {
    if (!this.contains(event.target)) this.close(false);
  }

  /**
   * Open Search Modal and add attributes, class and Events in DOM
   *
   * @param {event} Event instance
   */
  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.searchFormContainer.setAttribute('open', true);
    this.classList.add('open__modal');
    this.removeAttribute('style');
    document.body.addEventListener('click', this.onBodyClickEvent);
    Utility.trapFocus(this);
    const focusTarget = this.searchQuery || document.getElementById('Search-In-Modal-component') || document.getElementById('Search-In-Modal');
    if (focusTarget) Utility.forceFocus(focusTarget);
  }

  /**
   * Close Search Modal and remove attributes, class and Events in DOM
   *
   * @param {event} Event instance
   */
  close(focusToggle = true) {
    Utility.removeTrapFocus();
    this.searchFormContainer.removeAttribute('open');
    document.getElementsByTagName('site-overlay')[0].classList.remove('overlay--body')
    this.classList.remove('open__modal');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    const predictiveSearch = this.querySelector('predictive-search');
    if (predictiveSearch && typeof predictiveSearch.close === 'function') {
      predictiveSearch.close();
    }
  }
}
customElements.define('search-modal', SearchModal);

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.resultsContent = this.querySelector('.predictive-search-results__content');
    this.loadingState = this.querySelector('.predictive-search-results__loading');
    this.abortController = null;
    
    if (!this.input) return;

    this.isBound = false;
    this.onInput = this.debounce((event) => {
      this.onChange(event);
    }, 300);
    this.onFocus = () => {
      const searchTerm = this.input.value.trim();
      if (searchTerm.length > 0) {
        this.getSearchResults(searchTerm);
      } else {
        this.close();
      }
    };
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) {
        this.close();
      }
    };
  }

  connectedCallback() {
    if (!this.input || this.isBound) return;

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus);
    document.addEventListener('click', this.onDocumentClick);
    this.isBound = true;
  }

  disconnectedCallback() {
    if (!this.input || !this.isBound) return;

    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('focus', this.onFocus);
    document.removeEventListener('click', this.onDocumentClick);
    this.isBound = false;
  }

  onChange(event) {
    const searchTerm = event.target.value.trim();
    
    if (searchTerm.length === 0) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.open();
    if (this.resultsContent) {
      this.resultsContent.innerHTML = '';
    }
    this.showLoading();

    const url = `${window.routes.predictive_search}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=6`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.json();
      })
      .then((data) => {
        const products = data.resources?.results?.products || [];

        if (products.length === 0) {
          const safeSearchTerm = escapeHtml(searchTerm);
          this.resultsContent.innerHTML = `
            <div class="p-3 text-center text-muted">
              <p class="mb-3">Sorry, nothing found for "${safeSearchTerm}"</p>
              <a href="/collections/all" class="btn btn-primary">
                See all products
              </a>
            </div>
          `;
        } else {
          let html = '<p class="text-uppercase border-bottom pb-2 mb-3">Products</p><div class="predictive-search__products p-2">';

          products.forEach((product) => {
            const price = this.formatPrice(product.price);
            const compareAtPriceValue = this.compareAtPriceForDisplayedPrice(product);
            const compareAtPrice = this.formatPrice(compareAtPriceValue);
            const isOnSale = this.normalizePriceCents(compareAtPriceValue) > this.normalizePriceCents(product.price);
            const safeTitle = escapeHtml(product.title);
            const safeUrl = escapeHtml(product.url);
            const safeImageUrl = product.image ? escapeHtml(product.image) : '';
            const image = product.image
              ? `<img src="${safeImageUrl}" alt="${safeTitle}" width="60" height="60" loading="lazy" class="rounded">`
              : '';
            const priceMarkup = isOnSale
              ? `
                <div class="search-card-price-group">
                  <s class="search-card-price-original m-0 p-0">${compareAtPrice}</s>
                  <span class="search-card-price-sale m-0 p-0">${price}</span>
                </div>
              `
              : `
                <div class="search-card-price-group">
                  <span class="search-card-price-original m-0 p-0">${price}</span>
                </div>
              `;

            html += `
            <a href="${safeUrl}" class="predictive-search__item card card-product card-product-style-1 text-start rounded-0 border-0">
              ${image}
              <div class="ms-3 pt-3 d-flex flex-column">
                <h6 class="card-title mb-0 font-size-sm">${safeTitle}</h6>
                ${priceMarkup}
              </div>
            </a>
          `;
          });

          const viewAllHref = `/search?q=${encodeURIComponent(searchTerm)}`;
          html += `
          </div>
          <div class="text-center mt-3 mb-3">
            <a href="${escapeHtml(viewAllHref)}" class="btn btn-primary">
              View all results
            </a>
          </div>`;

          this.resultsContent.innerHTML = html;
        }

        this.hideLoading();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Predictive search error:', error);
        if (this.resultsContent) {
          this.resultsContent.innerHTML = `
            <div class="p-3 text-center text-muted">
              <p class="mb-0">Unable to load search results. Please try again.</p>
            </div>
          `;
        }
        this.hideLoading();
      });
  }

  formatPrice(cents) {
    const normalizedCents = this.normalizePriceCents(cents);
    if (Number.isNaN(normalizedCents)) return '';

    const moneyFormat = window.globalVariables?.money_format;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(normalizedCents, moneyFormat);
    }

    return (normalizedCents / 100).toFixed(2);
  }

  /**
   * Predictive search `product.price` is the displayed listing price (typically `price_min`).
   * Pair compare-at with that tier: prefer `compare_at_price_min` over `compare_at_price_max`
   * when showing the low price, so we do not strike through a high variant MSRP beside a low sale price.
   */
  compareAtPriceForDisplayedPrice(product) {
    if (product.compare_at_price != null && product.compare_at_price !== '') {
      return product.compare_at_price;
    }

    const displayed = this.normalizePriceCents(product.price);
    const priceMin = this.normalizePriceCents(product.price_min);
    const priceMax = this.normalizePriceCents(product.price_max);
    const minCap = product.compare_at_price_min;
    const maxCap = product.compare_at_price_max;

    const varies =
      !Number.isNaN(priceMin) && !Number.isNaN(priceMax) && priceMin !== priceMax;

    if (varies && !Number.isNaN(displayed) && !Number.isNaN(priceMax) && displayed === priceMax) {
      return maxCap ?? minCap;
    }

    return minCap ?? maxCap;
  }

  normalizePriceCents(cents) {
    if (cents == null) return NaN;

    if (typeof cents === 'string' && cents.includes('.')) {
      return Math.round(parseFloat(cents) * 100);
    }

    const numericValue = Number(cents);
    if (Number.isNaN(numericValue)) return NaN;
    return Number.isInteger(numericValue) ? numericValue : Math.round(numericValue * 100);
  }

  showLoading() {
    if (this.loadingState) {
      this.loadingState.setAttribute('data-loading', '');
    }
  }

  hideLoading() {
    if (this.loadingState) {
      this.loadingState.removeAttribute('data-loading');
    }
  }

  open() {
    if (this.resultsContainer) {
      this.resultsContainer.setAttribute('data-open', '');
    }
  }

  close() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.hideLoading();
    if (this.resultsContainer) {
      this.resultsContainer.removeAttribute('data-open');
    }
    if (this.resultsContent) {
      this.resultsContent.innerHTML = '';
    }
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);